import React, { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import axios from 'axios'
import { Box, Typography, CircularProgress } from '@material-ui/core'

const SATELLITE_TILE = 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'

const LeadMiniMap = ({ coordinates, leadId, apn, height = 220 }) => {
  const mapContainerRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [parcelInfo, setParcelInfo] = useState(null)

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || !coordinates?.latitude || !coordinates?.longitude) return
    if (mapInstanceRef.current) return // already initialized

    const map = L.map(mapContainerRef.current, {
      center: [coordinates.latitude, coordinates.longitude],
      zoom: 18,
      zoomControl: false,
      attributionControl: false,
      dragging: true,
      scrollWheelZoom: false,
      doubleClickZoom: true,
      boxZoom: false,
      keyboard: false
    })

    L.tileLayer(SATELLITE_TILE, {
      maxZoom: 21,
      maxNativeZoom: 20
    }).addTo(map)

    // Small marker at property coordinates
    L.circleMarker([coordinates.latitude, coordinates.longitude], {
      radius: 6,
      fillColor: '#ff3838',
      color: '#fff',
      weight: 2,
      fillOpacity: 0.9
    }).addTo(map)

    // Small zoom control bottom-right
    L.control.zoom({ position: 'bottomright' }).addTo(map)

    mapInstanceRef.current = map

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [coordinates])

  // Fetch parcel polygons
  useEffect(() => {
    if (!leadId || !coordinates?.latitude) return

    let cancelled = false
    setLoading(true)

    axios.get(`/api/company/leads/${leadId}/parcel`)
      .then(res => {
        if (cancelled) return
        if (res.data?.success && res.data.data?.length > 0) {
          setParcelInfo(res.data.data)

          const map = mapInstanceRef.current
          if (!map) return

          res.data.data.forEach(parcel => {
            if (!parcel.paths || parcel.paths.length === 0) return

            const latlngs = parcel.paths.map(p => [p.lat, p.lng])
            const polygon = L.polygon(latlngs, {
              color: '#0022FF',
              weight: 2.5,
              opacity: 0.9,
              fillColor: 'rgba(0, 34, 255, 0.15)',
              fillOpacity: 0.15
            }).addTo(map)

            // Fit the map to show the parcel
            map.fitBounds(polygon.getBounds(), { padding: [20, 20], maxZoom: 19 })
          })
        }
      })
      .catch(err => {
        if (!cancelled) console.error('Error fetching parcel for mini-map:', err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [leadId, coordinates])

  if (!coordinates?.latitude || !coordinates?.longitude) return null

  return (
    <Box>
      <Box
        position="relative"
        style={{
          height,
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid #ddd'
        }}
      >
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
        {loading && (
          <Box
            position="absolute"
            top={8}
            right={8}
            zIndex={1000}
            bgcolor="rgba(255,255,255,0.85)"
            borderRadius={4}
            px={1}
            py={0.5}
            display="flex"
            alignItems="center"
            gap={1}
          >
            <CircularProgress size={14} />
            <Typography variant="caption">Loading parcels...</Typography>
          </Box>
        )}
      </Box>
      {parcelInfo && parcelInfo.length > 0 && (
        <Box mt={0.5} display="flex" gap={2} flexWrap="wrap">
          {parcelInfo.map((p, i) => (
            <Box key={i}>
              {p.APN && (
                <Typography variant="caption" color="textSecondary">
                  APN: {p.APN}
                </Typography>
              )}
              {p.sf && (
                <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                  {Number(p.sf).toLocaleString()} sqft ({(p.area || 0).toFixed(2)} ac)
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default LeadMiniMap
